/**
 * Main Layout Component
 * 
 * Provides the main application layout with sidebar and app bar
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  People as PeopleIcon,
  Gavel as GavelIcon,
  Help as HelpIcon,
  AccountCircle,
  Settings,
  Logout,
  VpnKey,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { fetchRoles } from '../../store/slices/appSlice';
import { eatonBlue } from '../../theme/theme';
import { TourButton } from './TourButton';
import { CommonStrings, ESstrings } from '../../localisation/strings.en';
import { logoutUser } from '../../redux/Action';
import { LocalStorage } from '../../store/local-storage';
import { useSecurityActions } from '@brightlayer-ui/react-auth-shared';
import { TimerLeft } from '../../components/TimerLeft';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { AuthService } from '../../APIService/AuthService';
import useIdleTimeout from '../../hooks/useIdleTimeout';
import { REACT_APP_SESSION_TIMEOUT, userLoginData } from '../../localisation/ApiUrls.en';
import { BasicConfirmationPopup } from '../../components/confimationpopup';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 56;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  tourId?: string;
}

const topNavItems: NavItem[] = [
  { label: 'Customers', path: '/customers', icon: <PeopleIcon />, tourId: 'nav-customers' },
];

const bottomNavItems: NavItem[] = [
  { label: 'Legal', path: '/legal', icon: <GavelIcon />, tourId: 'nav-legal' },
  { label: 'Help', path: '/help', icon: <HelpIcon />, tourId: 'nav-help' },
];



const SESSION_TIMEOUT_MINUTES = parseInt(REACT_APP_SESSION_TIMEOUT, 10);
// TimerLeft.minutes prop accepts a value in seconds despite its name
const WARNING_COUNTDOWN_SECONDS = 2 * 60;

const TimerBasicConfirmationPopup: React.FC = () => {
  const navigate = useNavigate();
  const securityHelper = useSecurityActions();
  const dispatch = useDispatch();
  const { showTimeoutDialog, resetIdleTimer, immediateLogout } = useIdleTimeout(SESSION_TIMEOUT_MINUTES, WARNING_COUNTDOWN_SECONDS);

  const handleLogout = useCallback((): void => {
    LocalStorage.clearAuthCredentials();
    localStorage.removeItem(ESstrings.USER_PROFILE);
    securityHelper.onUserNotAuthenticated();
    dispatch(logoutUser([]));
    navigate('/custom-login-route');
  }, [securityHelper, dispatch, navigate]);

  // If the tab was backgrounded past the full timeout (idle + warning),
  // skip the warning dialog and log out immediately.
  useEffect(() => {
    if (immediateLogout) {
      handleLogout();
    }
  }, [immediateLogout, handleLogout]);


  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const userData = await userLoginData();
      const response: any = await AuthService.refreshToken({ refresh_token: userData?.refresh_token });

      if (!response.success) {
        throw new Error('Refresh token failed');
      }
      if (response?.data && response?.data?.data) {
        LocalStorage.userData({
          ...userData,
          ...response?.data?.data,
        });
        LocalStorage.setLoginTimestamp(moment().unix());
      }

      resetIdleTimer();
    } catch (error) {
      console.error('Error refreshing token:', error);
      handleLogout();
    }
  }, [resetIdleTimer, handleLogout]);

  const handleSessionDialogClose = (result: boolean): void => {
    if (result) {
      refreshToken();
    } else {
      handleLogout();
    }
  };

  return (

    <BasicConfirmationPopup
      title={ESstrings.SessionTimeout}
      message=""
      okBtnName={ESstrings.ContinueSession}
      cancelBtnName={ESstrings.LogOut}
      onClose={handleSessionDialogClose}
      open={showTimeoutDialog}
      ContentNode={
        showTimeoutDialog ? (
          <div style={{ textAlign: 'center' }}>
            <p>{CommonStrings.SessionTimeOutWarningMessage}</p>
            <div style={{ fontSize: 32, fontWeight: 'bold', marginTop: 8 }}>
              <TimerLeft minutes={WARNING_COUNTDOWN_SECONDS} timeout={handleLogout} />
            </div>
          </div>
        ) : null
      }
    />
  );
};


export const MainLayout: React.FC = () => {
  const securityHelper = useSecurityActions();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector(state => state.ui.sidebarOpen);

  // Fetch roles once on app load so they're available everywhere
  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);

  const storedUser = LocalStorage.getDetailsUserData();
  const firstName = (storedUser?.data?.first_name ?? '').toString();
  const lastName = (storedUser?.data?.last_name ?? '').toString();
  const userEmail = storedUser?.data?.email ?? '';
  const userName = `${firstName} ${lastName}`.trim();
  const userInitials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };
  const logOut = (): void => {
    LocalStorage.clearAuthCredentials();
    localStorage.removeItem(ESstrings.USER_PROFILE);
    securityHelper.onUserNotAuthenticated();
    dispatch(logoutUser([]));
    navigate('/');
    handleProfileMenuClose();
  };

  const changePassword = (): void => {
    securityHelper.showChangePassword();
    handleProfileMenuClose();
  }
  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: eatonBlue,
        }}
        data-tour="app-bar"
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open user menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
            data-tour="menu-button"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Eaton Smart Filter
          </Typography>

          <TourButton />

          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            sx={{ p: 0 }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'white',
                color: eatonBlue,
                fontWeight: 600,
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              {userInitials || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        id="profile-menu"
        anchorEl={profileMenuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(profileMenuAnchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 320,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        {/* User Profile Section */}
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#E3F2FD',
              color: eatonBlue,
              fontSize: '1.5rem',
              fontWeight: 600
            }}
          >
            {userInitials || 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
              {userName || 'User'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {userEmail}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Change Password */}
        <MenuItem
          onClick={changePassword}
          sx={{
            py: 1.5,
            px: 2.5,
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <ListItemIcon>
            <VpnKey sx={{ color: '#666' }} />
          </ListItemIcon>
          <ListItemText
            primary="Change Password"
            primaryTypographyProps={{
              fontSize: '0.95rem',
              color: '#333'
            }}
          />
        </MenuItem>

        <Divider />

        {/* Log Out */}
        <MenuItem
          onClick={logOut}
          sx={{
            py: 1.5,
            px: 2.5,
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <ListItemIcon>
            <Logout sx={{ color: '#666' }} />
          </ListItemIcon>
          <ListItemText
            primary="Log Out"
            primaryTypographyProps={{
              fontSize: '0.95rem',
              color: '#333'
            }}
          />
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        component="nav"
        role="navigation"
        sx={{
          width: sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
            boxSizing: 'border-box',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
          },
        }}
      >
        {/* Sidebar Header with Blue Background */}
        <Box
          sx={{
            backgroundColor: eatonBlue,
            color: 'white',
            height: 64,
            display: 'flex',
            alignItems: 'center',
            px: sidebarOpen ? 2 : 1,
          }}
        >
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{ mr: sidebarOpen ? 1 : 0 }}
          >
            <MenuIcon />
          </IconButton>
          {sidebarOpen && (
            <Typography variant="h6" noWrap sx={{ fontWeight: 500 }}>
              Eaton Smart Filter
            </Typography>
          )}
        </Box>

        {/* Sidebar Content */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#f5f5f5'
        }}>
          {/* Top Navigation Items */}
          <List sx={{ pt: 1 }}>
            {topNavItems.map((item) => {
              const isSelected = location.pathname === item.path || (item.path === '/customers' && location.pathname === '/');
              return (
              <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleNavigation(item.path)}
                  data-tour={item.tourId}
                  sx={{
                    minHeight: 48,
                    justifyContent: sidebarOpen ? 'initial' : 'center',
                    px: 2.5,
                    backgroundColor: isSelected ? '#1976D2' : 'transparent',
                    color: isSelected ? 'white' : '#616161',
                    '&:hover': {
                      backgroundColor: isSelected ? '#1976D2' : '#e0e0e0',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#1976D2',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#1565C0',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: sidebarOpen ? 2 : 'auto',
                      justifyContent: 'center',
                      color: isSelected ? 'white' : '#616161',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>

          {/* Spacer to push bottom items down */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Bottom Navigation Items */}
          <List>
            {bottomNavItems.map((item) => (
              <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                  data-tour={item.tourId}
                  sx={{
                    minHeight: 48,
                    justifyContent: sidebarOpen ? 'initial' : 'center',
                    px: 2.5,
                    color: '#616161',
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: sidebarOpen ? 2 : 'auto',
                      justifyContent: 'center',
                      color: '#616161',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Footer with Eaton Logo and Version */}
          {sidebarOpen && (
            <Box sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>

              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#616161' }}>
                  VERSION 1.0.0
                </Typography>
                <IconButton size="small" sx={{ p: 0.5 }}>
                  <Settings sx={{ fontSize: 16, color: '#616161' }} />
                </IconButton>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', color: '#9e9e9e', mt: 0.5 }}>
                Copyright © Eaton 2026
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#9e9e9e' }}>
                All Rights Reserved
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED}px)` },
          ml: 0,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
      <TimerBasicConfirmationPopup />
    </Box>
  );
};